import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { FaArrowLeft, FaArrowRight, FaChevronRight, FaExpand, FaPlay, FaTimes } from "react-icons/fa";
import "./CaseStudy.css";
import DarkMode from "../DarkMode/DarkMode";
import { nextCaseStudy } from "./caseStudies";

/**
 * Shared layout for every project case study. A page only supplies a `study` object
 * (see pages/CRMBuzz/CRMBuzzCaseStudy.jsx for the full shape); sections whose data is
 * missing are skipped, and the sticky navigation is built from the sections that exist.
 *
 * Markup note: this layout deliberately avoids bare <header> and <footer> elements. Header.css and
 * Footer.css style those tags globally for the home page (full-height flex hero, decorative blobs,
 * centred footer), and those rules would leak into any <header>/<footer> used here.
 */

const pad = (n) => String(n).padStart(2, "0");

// ---------------- Motion ----------------
const useReveal = () => {
  const reduced = useReducedMotion();
  return {
    initial: reduced ? false : { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.12 },
    transition: { duration: 0.5, ease: "easeOut" },
  };
};

// ---------------- Building blocks ----------------

/** Numbered section with the heading on the left and the content on the right (stacked on small screens). */
const Section = ({ id, index, eyebrow, title, intro, wide = false, children }) => {
  const reveal = useReveal();
  return (
    <motion.section id={id} className={`cs-section${wide ? " cs-section--wide" : ""}`} aria-labelledby={`${id}-title`} {...reveal}>
      <div className="container cs-section__grid">
        <div className="cs-section__head">
          <span className="cs-section__index" aria-hidden="true">{pad(index)}</span>
          <p className="cs-eyebrow">{eyebrow}</p>
          <h2 id={`${id}-title`} className="cs-section__title">{title}</h2>
          {intro && <p className="cs-section__intro">{intro}</p>}
        </div>
        <div className="cs-section__body">{children}</div>
      </div>
    </motion.section>
  );
};

const Prose = ({ paragraphs }) => (
  <div className="cs-prose">
    {paragraphs.map((p) => (
      <p key={p.slice(0, 40)}>{p}</p>
    ))}
  </div>
);

/** Horizontal numbered steps on desktop, vertical on small screens. */
const FlowRow = ({ steps }) => (
  <ol className="cs-flow">
    {steps.map((step, i) => (
      <li className="cs-flow__step" key={step}>
        <span className="cs-flow__num">{i + 1}</span>
        <span className="cs-flow__label">{step}</span>
        {i < steps.length - 1 && <FaChevronRight className="cs-flow__arrow" aria-hidden="true" />}
      </li>
    ))}
  </ol>
);

/** Vertical timeline used for the architecture diagrams. */
const Timeline = ({ label, steps }) => (
  <div className="cs-timeline cs-card">
    <h3 className="cs-timeline__label">{label}</h3>
    <ol>
      {steps.map((step, i) => (
        <li key={step}>
          <span className="cs-timeline__dot">{i + 1}</span>
          <span className="cs-timeline__text">{step}</span>
        </li>
      ))}
    </ol>
  </div>
);

const Media = ({ item }) =>
  item.type === "video" ? (
    <video src={item.src} controls preload="metadata" playsInline>
      Your browser doesn't support embedded video.
    </video>
  ) : (
    <img src={item.src} alt={item.alt || item.label || ""} />
  );

/** Hero media: one framed video/image, with tabs when a project has several. */
const MediaShowcase = ({ media }) => {
  const [active, setActive] = useState(0);
  const current = media[active];
  return (
    <div id="demo" className="cs-media">
      <div className="cs-media__bar">
        <span className="cs-media__dots" aria-hidden="true"><i /><i /><i /></span>
        {media.length > 1 ? (
          <div className="cs-media__tabs" role="tablist" aria-label="Demos">
            {media.map((m, i) => (
              <button
                key={m.label}
                type="button"
                role="tab"
                aria-selected={i === active}
                className={`cs-media__tab${i === active ? " is-active" : ""}`}
                onClick={() => setActive(i)}
              >
                {m.label}
              </button>
            ))}
          </div>
        ) : (
          <span className="cs-media__title">{current.label}</span>
        )}
      </div>
      <div className="cs-media__frame">
        <Media item={current} key={current.src} />
      </div>
    </div>
  );
};

/** Full-screen image viewer. Closes on Escape, on the close button, or on a click outside the image. */
const Lightbox = ({ image, onClose }) => {
  const closeRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [onClose]);

  return (
    <div className="cs-lightbox" role="dialog" aria-modal="true" aria-label={image.caption} onClick={onClose}>
      <button ref={closeRef} type="button" className="cs-lightbox__close" aria-label="Close image" onClick={onClose}>
        <FaTimes />
      </button>
      <figure onClick={(e) => e.stopPropagation()}>
        <img src={image.src} alt={image.caption} />
        <figcaption>{image.caption}</figcaption>
      </figure>
    </div>
  );
};

/** Highlights the navigation link of the section currently in view. */
const useActiveSection = (ids) => {
  const [active, setActive] = useState(ids[0]);
  const key = ids.join(",");
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-35% 0px -60% 0px" }
    );
    key.split(",").forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [key]);
  return active;
};

// ---------------- Page ----------------

const CaseStudy = ({ study }) => {
  const [zoomed, setZoomed] = useState(null);
  const closeZoom = useCallback(() => setZoomed(null), []);
  const reduced = useReducedMotion();
  const next = nextCaseStudy(study.slug);

  // Sections are numbered in the order they actually appear.
  const sections = [
    study.overview && { id: "overview", label: "Overview" },
    study.problem && { id: "problem", label: "Problem" },
    study.solution && { id: "solution", label: "Solution" },
    study.features?.length && { id: "features", label: "Features" },
    study.gallery?.length && { id: "screens", label: "Screens" },
    study.diagrams?.length && { id: "architecture", label: "Architecture" },
    study.challenges?.length && { id: "challenges", label: "Challenges" },
    study.stack?.length && { id: "stack", label: "Tech stack" },
  ].filter(Boolean);
  const indexOf = (id) => sections.findIndex((s) => s.id === id) + 1;
  const activeSection = useActiveSection(sections.map((s) => s.id));

  const heroMotion = reduced
    ? {}
    : { initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.55, ease: "easeOut" } };

  return (
    <div className="cs-page">
      <DarkMode />

      {/* ---------- Hero ---------- */}
      <div className="cs-hero">
        <div className="container">
          <Link to="/" className="cs-back">
            <FaArrowLeft size={12} /> Back to Portfolio
          </Link>

          <motion.div className="cs-hero__grid" {...heroMotion}>
            <div className="cs-hero__main">
              <div className="cs-hero__badges">
                {study.badges.map((b) => (
                  <span className="badge" key={b.label}>
                    {b.icon}
                    {b.label}
                  </span>
                ))}
              </div>
              <h1 className="cs-hero__title gradient-text">{study.title}</h1>
              <p className="cs-hero__subtitle">{study.subtitle}</p>
              <ul className="cs-tags" aria-label="Main technologies">
                {study.tags.map((t) => (
                  <li className="cs-tag" key={t}>{t}</li>
                ))}
              </ul>
              <div className="cs-hero__actions">
                {study.media?.length > 0 && (
                  <a href="#demo" className="btn">
                    {study.media[0].type === "video" ? <FaPlay size={11} /> : <FaExpand size={12} />}
                    {study.media[0].type === "video" ? "Watch the demo" : "See the product"}
                  </a>
                )}
                {study.demoUrl && (
                  <a href={study.demoUrl} className="btn btn-outline" target="_blank" rel="noreferrer">
                    Interactive demo
                  </a>
                )}
                <Link to="/#contact" className="btn btn-outline">Get in touch</Link>
              </div>
            </div>

            {study.facts?.length > 0 && (
              <aside className="cs-facts cs-card" aria-label="Project at a glance">
                <h2 className="cs-facts__title">At a glance</h2>
                <dl>
                  {study.facts.map((f) => (
                    <div className="cs-facts__row" key={f.label}>
                      <dt>{f.label}</dt>
                      <dd>{f.value}</dd>
                    </div>
                  ))}
                </dl>
              </aside>
            )}
          </motion.div>

          {study.media?.length > 0 && <MediaShowcase media={study.media} />}
        </div>
      </div>

      {/* ---------- Sticky section navigation ---------- */}
      <nav className="cs-nav" aria-label="Case study sections">
        <div className="container cs-nav__inner">
          <span className="cs-nav__project">{study.title}</span>
          <ul>
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className={activeSection === s.id ? "is-active" : undefined} aria-current={activeSection === s.id ? "true" : undefined}>
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      <main>
        {study.overview && (
          <Section id="overview" index={indexOf("overview")} eyebrow="Overview" title={study.overview.title}>
            <Prose paragraphs={study.overview.paragraphs} />
          </Section>
        )}

        {study.problem && (
          <Section id="problem" index={indexOf("problem")} eyebrow="Motivation" title="The problem">
            <Prose paragraphs={study.problem.paragraphs} />
          </Section>
        )}

        {study.solution && (
          <Section id="solution" index={indexOf("solution")} eyebrow="Approach" title="The solution" wide>
            <FlowRow steps={study.solution.steps} />
            {study.solution.note && <p className="cs-note">{study.solution.note}</p>}
          </Section>
        )}

        {study.features?.length > 0 && (
          <Section
            id="features"
            index={indexOf("features")}
            eyebrow="Capabilities"
            title="Key features"
            intro={`${study.features.length} capabilities that define the product.`}
            wide
          >
            <ul className="cs-features">
              {study.features.map((f, i) => (
                <li className="cs-feature cs-card" key={f.title}>
                  <span className="cs-feature__num" aria-hidden="true">{pad(i + 1)}</span>
                  <h3>{f.title}</h3>
                  <p>{f.description}</p>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {study.gallery?.length > 0 && (
          <Section id="screens" index={indexOf("screens")} eyebrow="Interface" title="Screens" intro={study.galleryNote} wide>
            <ul className="cs-gallery">
              {study.gallery.map((g) => (
                <li key={g.caption}>
                  <figure className="cs-shot cs-card">
                    <button type="button" className="cs-shot__button" onClick={() => setZoomed(g)} aria-label={`Enlarge: ${g.caption}`}>
                      <img src={g.src} alt={g.caption} loading="lazy" />
                      <span className="cs-shot__zoom" aria-hidden="true"><FaExpand /></span>
                    </button>
                    <figcaption>{g.caption}</figcaption>
                  </figure>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {study.diagrams?.length > 0 && (
          <Section id="architecture" index={indexOf("architecture")} eyebrow="System design" title="Architecture" wide>
            <div className="cs-diagrams">
              {study.diagrams.map((d) => (
                <Timeline key={d.label} label={d.label} steps={d.steps} />
              ))}
            </div>
          </Section>
        )}

        {study.challenges?.length > 0 && (
          <Section id="challenges" index={indexOf("challenges")} eyebrow="Engineering" title="Technical challenges" wide>
            <ol className="cs-challenges">
              {study.challenges.map((c, i) => (
                <li className="cs-challenge cs-card" key={c.title}>
                  <div className="cs-challenge__head">
                    <span className="cs-challenge__num gradient-text" aria-hidden="true">{pad(i + 1)}</span>
                    <h3>{c.title}</h3>
                  </div>
                  <div className="cs-challenge__cols">
                    <div className="cs-challenge__col">
                      <span className="cs-chip cs-chip--problem">Problem</span>
                      <p>{c.problem}</p>
                    </div>
                    <div className="cs-challenge__col">
                      <span className="cs-chip cs-chip--solution">Solution</span>
                      <p>{c.solution}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {study.stack?.length > 0 && (
          <Section id="stack" index={indexOf("stack")} eyebrow="Built with" title="Tech stack" wide>
            <div className="cs-stack">
              {study.stack.map((s) => (
                <div className="cs-stack__group cs-card" key={s.group}>
                  <h3>{s.group}</h3>
                  <ul className="cs-tags">
                    {s.items.map((item) => (
                      <li className="cs-tag" key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            {study.stackIcons?.length > 0 && (
              <ul className="cs-stack__icons" aria-label="Technologies">
                {study.stackIcons.map(([Icon, label]) => (
                  <li key={label}>
                    <Icon aria-hidden="true" />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        )}
      </main>

      {/* ---------- Footer ---------- */}
      <div className="cs-footer">
        <div className="container cs-footer__grid">
          <div className="cs-cta cs-card">
            <h2>Interested in the implementation details?</h2>
            <p>I'm happy to walk through the architecture, the trade-offs and the code.</p>
            <div className="cs-cta__actions">
              <Link to="/#contact" className="btn">Get in touch</Link>
              <Link to="/" className="btn btn-outline">
                <FaArrowLeft size={12} /> Back to Portfolio
              </Link>
            </div>
          </div>
          {next && (
            <Link to={`/projects/${next.slug}`} className="cs-next cs-card">
              <span className="cs-eyebrow">Next case study</span>
              <span className="cs-next__title">{next.title}</span>
              <span className="cs-next__summary">{next.summary}</span>
              <span className="cs-next__arrow" aria-hidden="true"><FaArrowRight /></span>
            </Link>
          )}
        </div>
      </div>

      {zoomed && <Lightbox image={zoomed} onClose={closeZoom} />}
    </div>
  );
};

export default CaseStudy;
